import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'
import clsx from 'clsx'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BodyText from 'LuxKit/Typography/BodyText'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import { useAppSelector } from 'hooks/reduxHooks'
import ContactContext from 'contexts/contactContext'
import { getBenefitCtaConfig } from 'luxPlus/lib/utils'
import LuxPlusLandingNavigationContext from 'luxPlus/contexts/LuxPlusLandingNavigationContext'
import LuxPlusLandingFilterRegistryContext from 'luxPlus/contexts/LuxPlusLandingFilterRegistryContext'
import { BENEFIT_VIEW_ICONS } from '../LuxPlusBenefitIcon'
import useScrollToId from 'hooks/useScrollToId'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'

const BenefitTile = styled.button`
  display: flex;
  align-items: center;
  gap: ${rem(16)};
  padding: ${rem(20)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.M};
  cursor: default;
  text-align: start;
  width: 100%;
  transition: background-color 0.15s;

  &.interactive {
    cursor: pointer;

    &:hover {
      background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    }
  }

  ${mediaQueryUp.tablet} {
    padding: ${rem(24)};
  }
`

const BenefitContent = styled.div`
  flex: 1;
  min-width: 0;
`

interface Props {
  benefit: App.LuxPlusBenefitView
}

function LuxPlusLandingBenefitTileItem({ benefit }: Props) {
  const ctaConfig = getBenefitCtaConfig(benefit.type)
  const supportContact = useContext(ContactContext)
  const navigationContext = useContext(LuxPlusLandingNavigationContext)
  const { hasFilterCategory } = useContext(LuxPlusLandingFilterRegistryContext)
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const { hasLoadedScrollElement, onScrollToId } = useScrollToId(ctaConfig?.scrollTarget ?? '', navigationContext.scrollOffset)

  const contextAction = ctaConfig?.navigationContextAction
    ? navigationContext[ctaConfig.navigationContextAction]
    : undefined

  const handleClick = useCallback(() => {
    if (contextAction) {
      contextAction()
      return
    }
    if (ctaConfig?.scrollTarget) {
      onScrollToId()
      if (ctaConfig.filterCategory) {
        const target = document.getElementById(ctaConfig.scrollTarget)
        target?.dispatchEvent(new CustomEvent('luxplus-filter-select', {
          detail: { category: ctaConfig.filterCategory },
        }))
      }
    }
  }, [onScrollToId, ctaConfig?.scrollTarget, ctaConfig?.filterCategory, contextAction])

  const filterCategoryAvailable = !ctaConfig?.filterCategory || !ctaConfig?.scrollTarget || hasFilterCategory(ctaConfig.scrollTarget, ctaConfig.filterCategory)

  const Icon = BENEFIT_VIEW_ICONS[benefit.type]
  const phoneTelUri = benefit.type === 'priority_support' && supportContact.local.number && canRedeemLuxPlusBenefits
    ? `tel:${supportContact.local.number}`
    : undefined
  const linkTo = phoneTelUri ?? ctaConfig?.path
  const hasAction = !!(filterCategoryAvailable && (linkTo || contextAction || (ctaConfig?.scrollTarget && hasLoadedScrollElement)))

  return (
    <BenefitTile
      as={linkTo ? 'a' : 'button'}
      href={linkTo}
      onClick={hasAction ? handleClick : undefined}
      className={clsx({ interactive: hasAction })}
    >
      <Icon colour="lux-plus" size="L" />
      <BenefitContent>
        <BodyText variant="medium" weight="bold">{benefit.shortTitle}</BodyText>
        {!!benefit.description && (
          <BodyText variant="medium" colour="light-foreground-alt">{benefit.description}</BodyText>
        )}
      </BenefitContent>
      {!!hasAction && <LineAngleRightIcon size="S" colour="light-foreground-alt" />}
    </BenefitTile>
  )
}

export default LuxPlusLandingBenefitTileItem
